import { useCallback, useEffect, useState, type FormEvent } from "react";
import { parseShareQr } from "../../../shared/qr";
import { api, ApiError, errorMessage, normalizeCode, pairUrl } from "../api";
import { Icon } from "../components/icons";
import { Modal } from "../components/Modal";
import { Qr } from "../components/Qr";
import { QrScanner } from "../components/QrScanner";

interface Props {
  notice: string | null;
  onEnter: (code: string, options?: { showQr?: boolean }) => void;
}

export function Home({ notice, onEnter }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeInput, setCodeInput] = useState("");
  const [pairToken, setPairToken] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);

  async function createSession() {
    setBusy(true);
    setError(null);
    try {
      const session = await api.createSession();
      onEnter(session.code, { showQr: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const joinByCode = useCallback(
    async (code: string) => {
      setBusy(true);
      setError(null);
      try {
        await api.getSession(code);
        onEnter(code);
      } catch (err) {
        setError(
          err instanceof ApiError && err.status === 404
            ? "Session nicht gefunden oder abgelaufen."
            : errorMessage(err),
        );
      } finally {
        setBusy(false);
      }
    },
    [onEnter],
  );

  async function join(event: FormEvent) {
    event.preventDefault();
    const code = normalizeCode(codeInput);
    if (code.length !== 6) {
      setError("Bitte gib den 6-stelligen Session-Code ein.");
      return;
    }
    await joinByCode(code);
  }

  const handleScan = useCallback(
    (text: string) => {
      setShowScanner(false);
      const parsed = parseShareQr(text);
      if (!parsed) {
        setError("Das ist kein Share-QR-Code.");
        return;
      }
      if (parsed.type === "pair") {
        // Beitritts-QR eines wartenden Rechners: dieses Gerät hat noch
        // keine Session, also automatisch eine neue starten und beide
        // Geräte hineinholen.
        void (async () => {
          setBusy(true);
          setError(null);
          try {
            const session = await api.createSession();
            await api.completePairing(parsed.token, session.code);
            onEnter(session.code);
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        })();
        return;
      }
      void joinByCode(parsed.code);
    },
    [joinByCode, onEnter],
  );

  // Pairing-QR dauerhaft anzeigen: Token beim Laden erzeugen, auf die
  // Verknüpfung durch ein verbundenes Gerät warten und abgelaufene Tokens
  // (nach 10 min) stillschweigend erneuern.
  useEffect(() => {
    let cancelled = false;
    let token: string | null = null;

    async function tick() {
      if (cancelled) return;
      try {
        if (!token) {
          token = (await api.createPairing()).token;
          if (!cancelled) setPairToken(token);
          return;
        }
        const status = await api.getPairing(token);
        if (status.status === "linked" && !cancelled) {
          onEnter(status.code);
        }
      } catch {
        token = null;
        if (!cancelled) setPairToken(null);
      }
    }

    void tick();
    const timer = setInterval(tick, 1500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [onEnter]);

  return (
    <main className="page narrow">
      <header className="hero">
        <h1>📦 Share</h1>
        <p>
          Dateien, Notizen und Zwischenablage – blitzschnell von Gerät zu
          Gerät.
        </p>
      </header>

      {notice && <div className="banner">{notice}</div>}
      {error && <div className="banner banner-error">{error}</div>}

      <section className="card home-card">
        <div className="pair-box">
          {pairToken ? (
            <Qr value={pairUrl(pairToken)} size={200} />
          ) : (
            <div
              className="qr qr-placeholder"
              style={{ width: 200, height: 200 }}
            />
          )}
          <p className="muted">
            Scanne diesen Code mit einem Gerät, um eine Session zu erstellen
            oder einer bestehenden beizutreten.
          </p>
        </div>

        <div className="divider">oder</div>

        <form className="join-form" onSubmit={join}>
          <input
            className="code-input"
            value={codeInput}
            onChange={(event) => setCodeInput(event.target.value)}
            placeholder="Code, z. B. ABC-123"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            maxLength={7}
            aria-label="Session-Code"
          />
          <button className="btn primary" type="submit" disabled={busy}>
            Beitreten
          </button>
        </form>

        <div className="home-actions">
          <button className="btn ghost" onClick={createSession} disabled={busy}>
            Neue Session starten
          </button>
          <button className="btn ghost" onClick={() => setShowScanner(true)}>
            <Icon name="qr-code-scan" /> Scannen
          </button>
        </div>
      </section>

      <p className="home-foot muted">
        Alles bleibt im Arbeitsspeicher · Session endet nach 1 h ohne
        Aktivität
      </p>

      {showScanner && (
        <Modal title="QR-Code scannen" onClose={() => setShowScanner(false)}>
          <QrScanner onResult={handleScan} />
          <p className="muted">
            Scanne den QR-Code einer Session oder eines wartenden Geräts.
          </p>
        </Modal>
      )}
    </main>
  );
}
